/**
 * useOrderForm - Custom hook for OrderForm logic and state management
 */
import { useState, useEffect } from 'react';
import { PositionInfo } from '../../types/portfolio';
import { OrderSide } from './types';
import { loadAccountInfo, loadPositions, fetchCurrentPrice, submitMarketOrder } from './orderFormApi';
import { getValidationError, isOrderValid as validateOrder } from './orderFormValidation';

export const useOrderForm = (onOrderSuccess?: () => void) => {
  const [symbol, setSymbol] = useState('');
  const [quantity, setQuantity] = useState('');
  const [side, setSide] = useState<OrderSide>('buy');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [positions, setPositions] = useState<PositionInfo[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<PositionInfo | null>(null);
  const [accountCash, setAccountCash] = useState<number>(0);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [estimatedAmount, setEstimatedAmount] = useState<number>(0);

  useEffect(() => {
    const loadData = async () => {
      if (side === 'sell') {
        setPositionsLoading(true);
        try {
          const [cash, positionsList] = await Promise.all([
            loadAccountInfo(),
            loadPositions()
          ]);
          setAccountCash(cash);
          setPositions(positionsList);
        } catch (err) {
          console.error('Failed to load positions:', err);
          setPositions([]);
        } finally {
          setPositionsLoading(false);
        }
      } else {
        try {
          const cash = await loadAccountInfo();
          setAccountCash(cash);
        } catch (err) {
          console.error('Failed to load account info:', err);
        }
      }
    };
    loadData();
  }, [side]);

  useEffect(() => {
    if (!symbol.trim()) {
      setCurrentPrice(null);
      setEstimatedAmount(0);
      return;
    }

    const timer = setTimeout(async () => {
      setPriceLoading(true);
      const price = await fetchCurrentPrice(symbol, 'stock');
      setCurrentPrice(price);
      setPriceLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [symbol]);

  useEffect(() => {
    const qty = parseFloat(quantity);
    if (currentPrice && !isNaN(qty) && qty > 0) {
      setEstimatedAmount(currentPrice * qty);
    } else {
      setEstimatedAmount(0);
    }
  }, [quantity, currentPrice]);

  const handleSelectPosition = (position: PositionInfo) => {
    setSelectedPosition(position);
    setSymbol(position.symbol);
    const formattedQty = position.qty.toFixed(8).replace(/\.?0+$/, '');
    setQuantity(formattedQty);
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!symbol.trim()) {
      setError('종목 코드를 입력하세요');
      return;
    }

    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) {
      setError('올바른 수량을 입력하세요');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await submitMarketOrder(symbol, qty, side, 'stock');
      showNotification(`${side === 'buy' ? '매수' : '매도'} 주문 성공: ${symbol.toUpperCase()} ${qty}주`, 'success');
      setSymbol('');
      setQuantity('');
      setCurrentPrice(null);
      setEstimatedAmount(0);
      if (onOrderSuccess) onOrderSuccess();
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return {
    symbol, setSymbol, quantity, setQuantity, side, setSide,
    loading, error, showToast, toastMessage, toastType,
    positions, positionsLoading, selectedPosition, accountCash,
    currentPrice, priceLoading, estimatedAmount,
    handleSelectPosition,
    getValidationError: () => getValidationError(symbol, quantity, side, selectedPosition, estimatedAmount, accountCash),
    isOrderValid: () => validateOrder(symbol, quantity, side, selectedPosition, estimatedAmount, accountCash),
    handleSubmit,
  };
};
